/* eslint-disable no-nested-ternary */
import {
  BaseField,
  DisplayIf,
  Field,
  FormLayout,
  HookForm,
} from '@/components_v2/HookForm';
import { QuestionTypes, StepTypes } from '@/gql/graphql';
import { useModals } from '@/hooks/ModalManager';
import { useCurrentAutoFlow } from '@/screens/Tickets/hooks/useCurrentAutoFlow';
import { Button, chakra, Divider, Spinner, useToast } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  AutomationPropertySelect,
  FormularySelect,
  LocalSelect,
  QuestionSelect,
  UserSelect,
} from '../Selects';
import { makeStepFormSchema, StepSchema } from './form-schemas';
import { ActionTable, TriggerTable } from './inputs';
import { Callout } from './misc';
import {
  StepFormParams,
  StepFormProvider,
  useStepForm,
  useStepFormContext,
} from './use-step-form';
import { getSubdomain } from '@/utils/getSubdomain';

function Fields({ automationFlowId }: { automationFlowId?: string }) {
  const { t } = useTranslation();
  const form = useFormContext<StepSchema>();
  const toast = useToast();
  const { automationFlowId: contextAutomationFlowId } = useStepFormContext();

  return (
    <FormLayout spacing={5} flex="1">
      <Field<StepSchema> name="name" label={t('form_labels.step_name')} type="text" />

      <Field<StepSchema>
        type="native-select"
        name="stepType"
        defaultValue={undefined}
        label={t('form_labels.step_type')}
        options={[
          { label: '', value: '' },
          { label: t('form_types.opening'), value: StepTypes.Opening },
          { label: t('form_types.progressing'), value: StepTypes.Progressing },
          { label: t('form_types.finishing'), value: StepTypes.Finishing },
          { label: t('form_types.create_ticket'), value: StepTypes.CreateTicket },
          { label: t('form_types.canceling'), value: StepTypes.Canceling },
        ]}
      />

      <Field<StepSchema>
        type="switch"
        name="autoFinish"
        label={t('placeholders.auto_finish_step')}
        size="lg"
        defaultValue=""
      />

      <FormLayout spacing={5} flex="1">
        <Callout>
          {t('callouts.activations_description')}
        </Callout>

        <BaseField name="triggers" flexGrow={0}>
          <TriggerTable automationFlowId={automationFlowId} />
        </BaseField>
      </FormLayout>

      <Callout>{t('callouts.actions_description')}</Callout>

      <BaseField name="actions" flexGrow={0}>
        <ActionTable />
      </BaseField>

      <DisplayIf<StepSchema>
        name="stepType"
        condition={(value) =>
          !!value &&
          ![
            StepTypes.Canceling,
            StepTypes.Finishing,
            StepTypes.CreateTicket,
          ].includes(`${value}` as StepTypes)
        }
      >
        <FormLayout spacing={5} flex="1">
          <Callout>
            {t('callouts.step_question_description')}
          </Callout>
          {/* Checklist da etapa */}
          <Controller
            name="taskForm"
            control={form.control}
            shouldUnregister
            render={({ field }) => (
              <BaseField
                name="taskForm"
                label={t('form_labels.step_checklist')}
                flexGrow={0}
              >
                <FormularySelect
                  selectionMode="single"
                  value={field.value?.map((e) => e!.id)}
                  initialOptions={(field.value || []) as any}
                  onChange={(keys) => {
                    setTimeout(() => {
                      field.onChange(keys?.length ? keys : undefined);
                    }, 1);
                  }}
                />
              </BaseField>
            )}
          />

          {/* Tipo do responsável */}
          <chakra.div display="flex" flexDir="column" gap={5}>
            <Field
              type="radio"
              name="assignType"
              label={t('form_labels.step_responsible')}
              size="lg"
              defaultValue=""
              options={[
                { value: 'none', label: t('step_options.undefined') },
                {
                  value: 'member_task',
                  label: t('step_options.responsible_opening'),
                },
                { value: 'user_id', label: t('step_options.specific_person') },
                { value: 'member_question', label: t('step_options.inherit_user_question') },
                {
                  value: 'last_member_task',
                  label: t('step_options.inherit_last_task'),
                },
              ]}
            />

            {/* Responsável */}
            <DisplayIf<StepSchema>
              name="assignType"
              condition={(value) => value === 'user_id'}
            >
              <Controller
                name="members"
                control={form.control}
                shouldUnregister
                render={({ field }) => (
                  <BaseField
                    name="members"
                    label={t('form_labels.step_responsible_person')}
                    flexGrow={0}
                  >
                    <UserSelect
                      selectionMode="single"
                      value={field.value?.map((e) => e!.id)}
                      initialOptions={(field.value || []) as any}
                      onChange={(keys) => {
                        field.onChange(keys?.length ? keys : undefined);
                      }}
                    />
                  </BaseField>
                )}
              />
            </DisplayIf>

            {/* Herdar de pergunta */}
            <DisplayIf<StepSchema>
              name="assignType"
              condition={(value) => value === 'member_question'}
            >
              <Controller
                name="memberQuestions"
                control={form.control}
                shouldUnregister
                render={({ field }) => (
                  <BaseField
                    name="memberQuestions"
                    label={t('form_labels.inherit_user_question')}
                    flexGrow={0}
                  >
                    <QuestionSelect
                      selectionMode="single"
                      questionTypes={[QuestionTypes.User]}
                      value={field.value?.map((e) => e!.id)}
                      initialOptions={(field.value || []) as any}
                      onChange={(keys) => {
                        field.onChange(keys?.length ? keys : undefined);
                      }}
                    />
                  </BaseField>
                )}
              />
            </DisplayIf>
          </chakra.div>

          {['jrmagro', 'demo'].includes(getSubdomain()) && (
            <chakra.div display="flex" flexDir="column" gap={2}>
              <chakra.label fontWeight="semibold" color="gray.600" mb={1}>
                Enviar tarefa pendente para responsável
              </chakra.label>
              <Field<StepSchema>
                type="checkbox"
                name="sendEmail"
                label="Enviar e-mail"
                size="lg"
                defaultValue={false as any}
                sx={{
                  '.chakra-checkbox__control': {
                    borderRadius: 'full',
                    '&[data-checked]': {
                      svg: { display: 'none' },
                      _before: {
                        content: '""',
                        display: 'inline-block',
                        position: 'relative',
                        width: '50%',
                        height: '50%',
                        borderRadius: '50%',
                        bg: 'currentColor',
                      },
                    },
                  },
                }}
              />
            </chakra.div>
          )}

          {/* Tipo de local */}
          <chakra.div display="flex" flexDir="column" gap={5}>
            <DisplayIf<StepSchema>
              name="taskForm"
              isExact
              condition={(value) => {
                return !!value;
              }}
            >
              <Field<StepSchema>
                type="radio"
                name="localType"
                defaultValue={undefined}
                label={t('form_labels.checklist_location')}
                size="lg"
                options={[
                  { label: t('step_options.none'), value: 'none' },
                  {
                    label: t('step_options.inherit_location_opening'),
                    value: 'local_opening_task',
                  },
                  {
                    label: t('step_options.inherit_location_attribute'),
                    value: 'local_ticket_property',
                  },
                  { label: t('step_options.select_specific_location'), value: 'local_id' },
                ]}
              />
            </DisplayIf>

            {/* Local do checklist */}
            <DisplayIf<StepSchema>
              name="localType"
              isExact
              condition={(value) => {
                return value === 'local_id';
              }}
            >
              <>
                <Controller
                  name="localSet"
                  control={form.control}
                  shouldUnregister
                  render={({ field }) => (
                    <BaseField
                      name="localSet"
                      label={t('form_labels.checklist_execution_location')}
                      flexGrow={0}
                    >
                      <LocalSelect
                        selectionMode="single"
                        value={field.value?.map((e) => e!.id)}
                        initialOptions={(field.value || []) as any}
                        onChange={(keys) => {
                          field.onChange(keys?.length ? keys : undefined);
                        }}
                      />
                    </BaseField>
                  )}
                />
                <Divider />
              </>
            </DisplayIf>

            {/* Local a partir de atributo */}
            <DisplayIf<StepSchema>
              name="localType"
              condition={(value) => value === 'local_ticket_property'}
            >
              <Controller
                name="localTicketProperty"
                control={form.control}
                shouldUnregister
                render={({ field }) => (
                  <BaseField
                    name="localTicketProperty"
                    label={t('form_labels.select_location_attribute')}
                    flexGrow={0}
                  >
                    <AutomationPropertySelect
                      selectionMode="single"
                      automationFlowId={automationFlowId}
                      value={field.value?.map((e) => e!.id)}
                      initialOptions={(field.value || []) as any}
                      onChange={(keys) => {
                        field.onChange(keys?.length ? keys : undefined);
                      }}
                    />
                  </BaseField>
                )}
              />
            </DisplayIf>
          </chakra.div>
        </FormLayout>
      </DisplayIf>
    </FormLayout>
  );
}

export function CreateStepForm({
  automationFlowId,
  stepId,
  position,
}: StepFormParams) {
  const toast = useToast();
  const { t } = useTranslation();

  const hook = useStepForm({
    automationFlowId,
    stepId,
    position,
  });
  const { defaultValue, loadingStepData, onSubmit, loading } = hook;

  if (loadingStepData)
    return (
      <chakra.div
        display="flex"
        h="container.sm"
        flex="1"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </chakra.div>
    );

  return (
    <StepFormProvider value={hook}>
      <chakra.fieldset flex="1" display="flex" disabled={loading}>
        <HookForm<StepSchema>
          autoComplete="off"
          position="relative"
          id="step-form"
          w="full"
          resolver={yupResolver(makeStepFormSchema())}
          schema={makeStepFormSchema()}
          onError={(error) => {
            const errorArray = Object.values(error);

            toast({
              title: t('validations.verify_form'),
              description: (
                <>
                  {errorArray
                    .map((error) => error.message)
                    .map((e) => (
                      <p>{e}</p>
                    ))}
                </>
              ),
              status: 'warning',
            });
          }}
          defaultValues={defaultValue}
          onSubmit={(values) => {
            onSubmit(values);
          }}
        >
          <chakra.div
            display="flex"
            justifyContent="space-between"
            flexDir="column"
            w="full"
            overflow="visible"
            maxH="100%"
            h="full"
          >
            <chakra.div maxW="100%" gap="6" p="6" flexDir="column">
              <Fields />
            </chakra.div>
          </chakra.div>
        </HookForm>
      </chakra.fieldset>
    </StepFormProvider>
  );
}

export function useCreateStepForm() {
  const { t } = useTranslation();
  const modals = useModals();
  const { id } = useCurrentAutoFlow();

  const openForm = (params: Omit<StepFormParams, 'automationFlowId'>) => {
    modals.open({
      id: 'board-create-form',
      title: t('tickets_module.step_modal_title'),
      size: '2xl',
      closeOnEsc: false,
      closeOnOverlayClick: false,

      footer: (
        <chakra.div h="auto" w="full" position="sticky">
          <Button
            type="submit"
            form="step-form"
            w="full"
            colorScheme="primary"
            size="lg"
          >
            {t('tickets_module.save')}
          </Button>
        </chakra.div>
      ),
      body: <CreateStepForm {...{ ...params, automationFlowId: id }} />,
    });
  };
  return openForm;
}
